import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper,
  Typography, Button, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Alert, Snackbar, Switch, FormControlLabel, Autocomplete,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  fetchLeaveTypes, fetchLeavePeriods, assignLeave,
} from '../../store/slices/leaveSlice';
import { fetchEmployees } from '../../store/slices/employeeSlice';
import { AppDispatch, RootState } from '../../store/store';
import { leaveApi } from '../../api/leaveApi';
import { calendarApi } from '../../api/calendarApi';
import { shiftApi } from '../../api/shiftApi';
import { AssignLeave, LeaveRequestDto } from '../../types';
import { toast } from 'react-toastify';

const DUPLICATE_LEAVE_MESSAGE = 'Leave already assigned for the selected date range.';
const SHIFT_NOT_ASSIGNED_MESSAGE = 'Shift is not assigned to the selected employee';

const toDateValue = (value: string): number => new Date(`${value}T00:00:00`).getTime();
const parseDateInputValue = (value: string) => new Date(`${value}T00:00:00`);
const formatDateKey = (value: Date | string) => {
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isInactiveLeaveRequest = (request: LeaveRequestDto): boolean => {
  const normalizedStatus = (request.statusText || '').trim().toLowerCase();
  return normalizedStatus === 'rejected' || normalizedStatus === 'cancelled';
};

const hasOverlappingLeave = (requests: LeaveRequestDto[], fromDate: string, toDate: string): boolean => {
  const selectedStart = toDateValue(fromDate);
  const selectedEnd = toDateValue(toDate);

  return requests.some((request) => {
    if (isInactiveLeaveRequest(request)) {
      return false;
    }

    const requestStart = toDateValue(request.startDate.split('T')[0]);
    const requestEnd = toDateValue(request.endDate.split('T')[0]);
    return selectedStart <= requestEnd && selectedEnd >= requestStart;
  });
};

const getWeekOfMonth = (date: Date): number => {
  const day = date.getDate();
  return Math.floor((day - 1) / 7) + 1;
};

const isDateMatchedByWeekOffRule = (
  date: Date,
  weekOffRules: Array<{ dayOfWeek: number; weekOfMonth: number }>
) => {
  const backendDayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
  const weekOfMonth = getWeekOfMonth(date);
  return weekOffRules.some((rule) => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
};

const AssignLeavePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { leaveTypes, leavePeriods, loading } = useSelector((state: RootState) => state.leave);
  const { employees } = useSelector((state: RootState) => state.employees);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [employeeId, setEmployeeId] = useState('');
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leavePeriodId, setLeavePeriodId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [numberOfDays, setNumberOfDays] = useState(1);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayType, setHalfDayType] = useState('');
  const [reason, setReason] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [employeesWithAssignedShift, setEmployeesWithAssignedShift] = useState<Set<string>>(new Set());
  // Leave balance state
  const [leaveBalance, setLeaveBalance] = useState<number | null>(null);
  const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(false);
  // Fetch leave balance when employee, period, or type changes
  useEffect(() => {
    // Only fetch if all required fields are selected
    if (employeeId && leavePeriodId && leaveTypeId) {
      setLeaveBalanceLoading(true);
      leaveApi.getLeaveBalance({ employeeId, leavePeriodId })
        .then((balances) => {
          // Find the balance for the selected leave type
          const found = balances.find(b => b.leaveTypeId === leaveTypeId);
          setLeaveBalance(found ? found.remainingDays : null);
        })
        .catch(() => {
          setLeaveBalance(null);
        })
        .finally(() => setLeaveBalanceLoading(false));
    } else {
      setLeaveBalance(null);
    }
  }, [employeeId, leavePeriodId, leaveTypeId]);

  const selectedLeavePeriod = useMemo(
    () => leavePeriods.find((period) => period.id === leavePeriodId),
    [leavePeriods, leavePeriodId]
  );
  const selectedLeaveType = useMemo(
    () => leaveTypes.find((type) => type.id === leaveTypeId),
    [leaveTypes, leaveTypeId]
  );
  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === employeeId) || null,
    [employees, employeeId]
  );
  const isShiftAssigned = Boolean(
    selectedEmployee?.shiftId || (selectedEmployee && employeesWithAssignedShift.has(selectedEmployee.id))
  );

  const loadEmployeesWithAssignedShift = useCallback(async () => {
    try {
      const currentShiftMappings = await shiftApi.getEmployeesWithShifts();
      setEmployeesWithAssignedShift(
        new Set(
          (currentShiftMappings || [])
            .filter((mapping) => mapping.isCurrent)
            .map((mapping) => mapping.employeeId)
        )
      );
    } catch {
      setEmployeesWithAssignedShift(new Set());
    }
  }, []);

  useEffect(() => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeavePeriods());
    dispatch(fetchEmployees({ page: 1, pageSize: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (dialogOpen) {
      loadEmployeesWithAssignedShift();
    }
  }, [dialogOpen, loadEmployeesWithAssignedShift]);

  useEffect(() => {
    if (validationMessage === SHIFT_NOT_ASSIGNED_MESSAGE && (!employeeId || isShiftAssigned)) {
      setValidationMessage('');
    }
  }, [employeeId, isShiftAssigned, validationMessage]);

  // Auto-calculate days
  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    if (isHalfDay) {
      setNumberOfDays(0.5);
      return;
    }

    const fallbackDiff = () => {
      const start = parseDateInputValue(startDate);
      const end = parseDateInputValue(endDate);
      const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setNumberOfDays(Math.max(1, diff));
    };

    if (!employeeId || !selectedLeaveType) {
      fallbackDiff();
      return;
    }

    let isActive = true;

    const calculateDays = async () => {
      try {
        const years = Array.from(
          new Set([parseDateInputValue(startDate).getFullYear(), parseDateInputValue(endDate).getFullYear()])
        );

        const [calendarStatuses, weekOffRules, holidayGroups] = await Promise.all([
          calendarApi.getEmployeeCalendarStatus(employeeId, startDate, endDate),
          calendarApi.getEmployeeWeekOffRules(employeeId),
          Promise.all(years.map((year) => leaveApi.getHolidays({ year }))),
        ]);

        const holidayDateKeys = new Set(
          holidayGroups
            .flat()
            .filter((holiday: any) => holiday.isActive)
            .map((holiday: any) => formatDateKey(holiday.date))
        );

        const statusByDate = new Map(
          calendarStatuses.map((item) => [formatDateKey(item.date), (item.status || '').trim().toLowerCase()])
        );

        let totalDays = 0;
        const current = parseDateInputValue(startDate);
        const end = parseDateInputValue(endDate);

        while (current <= end) {
          const dateKey = formatDateKey(current);
          const calendarStatus = statusByDate.get(dateKey);
          const isHoliday = calendarStatus === 'holiday' || holidayDateKeys.has(dateKey);
          const isWeekOff = calendarStatus === 'weekoff' || isDateMatchedByWeekOffRule(current, weekOffRules);

          if (!selectedLeaveType.includeHolidays && isHoliday) {
            current.setDate(current.getDate() + 1);
            continue;
          }

          if (!selectedLeaveType.includeWeekends && isWeekOff) {
            current.setDate(current.getDate() + 1);
            continue;
          }

          totalDays += 1;
          current.setDate(current.getDate() + 1);
        }

        if (isActive) {
          setNumberOfDays(Math.max(totalDays, 0));
        }
      } catch {
        if (isActive) {
          fallbackDiff();
        }
      }
    };

    calculateDays();

    return () => {
      isActive = false;
    };
  }, [employeeId, endDate, isHalfDay, selectedLeaveType, startDate]);

  useEffect(() => {
    if (!selectedLeavePeriod) {
      return;
    }

    if (startDate && (startDate < selectedLeavePeriod.startDate || startDate > selectedLeavePeriod.endDate)) {
      setStartDate('');
    }

    if (endDate && (endDate < selectedLeavePeriod.startDate || endDate > selectedLeavePeriod.endDate)) {
      setEndDate('');
    }
  }, [selectedLeavePeriod, startDate, endDate]);

  const resetForm = () => {
    setEmployeeId('');
    setLeaveTypeId('');
    setLeavePeriodId('');
    setStartDate('');
    setEndDate('');
    setNumberOfDays(0);
    setIsHalfDay(false);
    setHalfDayType('');
    setReason('');
    setValidationMessage('');
  };

  const handleSave = async () => {
    setValidationMessage('');

    if (!employeeId || !leaveTypeId || !leavePeriodId || !startDate || !endDate || !reason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!isShiftAssigned) {
      setValidationMessage(SHIFT_NOT_ASSIGNED_MESSAGE);
      return;
    }

    if (endDate < startDate) {
      toast.error('End date cannot be earlier than start date');
      return;
    }
    
    if (selectedLeavePeriod) {
      if (startDate < selectedLeavePeriod.startDate || endDate > selectedLeavePeriod.endDate) {
        toast.error(`Leave dates must be within the selected leave period (${selectedLeavePeriod.startDate} to ${selectedLeavePeriod.endDate})`);
        return;
      }
    }

    if (isHalfDay && !halfDayType) {
      toast.error('Please select half day type');
      return;
    }

    if (startDate === endDate) {
      try {
        const [calendarStatus, weekOffRules] = await Promise.all([
          calendarApi.getEmployeeCalendarStatus(employeeId, startDate, endDate),
          calendarApi.getEmployeeWeekOffRules(employeeId),
        ]);

        const selectedDayStatus = (calendarStatus[0]?.status || '').trim().toLowerCase();
        if (selectedDayStatus === 'holiday') {
          const holidayMessage = 'Selected date is a holiday.';
          //setValidationMessage(holidayMessage);
          toast.error(holidayMessage);
          return;
        }

        const selectedDateObj = new Date(`${startDate}T00:00:00`);
        const backendDayOfWeek = selectedDateObj.getDay() === 0 ? 7 : selectedDateObj.getDay();
        const weekOfMonth = getWeekOfMonth(selectedDateObj);
        const isWeekOffByRule = weekOffRules.some(
          (rule) => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth
        );

        if (selectedDayStatus === 'weekoff' || isWeekOffByRule) {
          const weekOffMessage = 'Selected date is a WeekOff.';
          //setValidationMessage(weekOffMessage);
          toast.error(weekOffMessage);
          return;
        }
      } catch {
        toast.error('Unable to validate holiday/week off for selected date');
        return;
      }
    }

    try {
      const existingRequests = await leaveApi.getLeaveRequests({ employeeId });

      if (hasOverlappingLeave(existingRequests, startDate, endDate)) {
        setValidationMessage(DUPLICATE_LEAVE_MESSAGE);
        toast.error(DUPLICATE_LEAVE_MESSAGE);
        return;
      }
    } catch {
      toast.error('Unable to validate existing leave assignments');
      return;
    }

    const data: AssignLeave = {
      employeeId,
      leaveTypeId,
      leavePeriodId,
      startDate,
      endDate,
      numberOfDays,
      isHalfDay,
      halfDayType: isHalfDay ? halfDayType : undefined,
      reason,
    };

    try {
      await dispatch(assignLeave(data)).unwrap();
      toast.success('Leave assigned successfully');
      setDialogOpen(false);
      resetForm();
    } catch (err: any) {
      toast.error(err || 'Failed to assign leave');
    }
  };

  const activePeriods = leavePeriods.filter(p => p.isActive);
  const activeTypes = leaveTypes.filter(t => t.isActive);

  // Enable save button only if all required fields are filled
  const isFormValid =
    employeeId &&
    isShiftAssigned &&
    leaveTypeId &&
    leavePeriodId &&
    startDate &&
    endDate &&
    reason.trim() &&
    (!isHalfDay || halfDayType);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Assign Leave</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }} sx={{ textTransform: 'none' }}>
          Assign Leave
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" color="text.secondary" mb={2}>
          Administrators can assign leave to employees directly. The leave will be auto-approved.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select an employee, choose the leave type and period, set the dates, and provide a reason. The leave balance will be automatically updated.
        </Typography>
      </Paper>

      {/* Assign Leave Dialog */}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Leave to Employee</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {validationMessage && <Alert severity="error">{validationMessage}</Alert>}
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.fullName} (${option.employeeCode})`}
              value={selectedEmployee}
              onChange={(_, newVal) => {
                setEmployeeId(newVal?.id || '');
                setValidationMessage(
                  newVal && !(newVal.shiftId || employeesWithAssignedShift.has(newVal.id))
                    ? SHIFT_NOT_ASSIGNED_MESSAGE
                    : ''
                );
              }}
              renderInput={(params) => <TextField {...params} label="Employee *" size="small" />}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Leave Period *</InputLabel>
              <Select value={leavePeriodId} label="Leave Period *" onChange={(e) => {
                setLeavePeriodId(e.target.value);
                setStartDate('');
                setEndDate('');
                setValidationMessage('');
              }}>
                {activePeriods.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Leave Type *</InputLabel>
              <Select value={leaveTypeId} label="Leave Type *" onChange={(e) => {
                setLeaveTypeId(e.target.value);
                const nextType = leaveTypes.find((t) => t.id === e.target.value);
                if (!nextType?.allowHalfDay) {
                  setIsHalfDay(false);
                  setHalfDayType('');
                }
                setValidationMessage('');
              }}>
                {activeTypes.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Leave Balance Display */}
            {employeeId && leavePeriodId && leaveTypeId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
                {leaveBalanceLoading ? (
                  <CircularProgress size={18} />
                ) : leaveBalance !== null ? (
                  <Alert severity={leaveBalance > 0 ? 'info' : 'warning'} sx={{ p: 1, m: 0, fontSize: 14 }}>
                    Available Leave Balance: <b>{leaveBalance}</b> day{leaveBalance === 1 ? '' : 's'}
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ p: 1, m: 0, fontSize: 14 }}>
                    Leave balance not available.
                  </Alert>
                )}
              </Box>
            )}

            <Box display="flex" gap={2}>
              <TextField size="small" fullWidth label="Start Date *" type="date" value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setValidationMessage('');
                }} InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: selectedLeavePeriod?.startDate,
                  max: selectedLeavePeriod?.endDate,
                }}
              />
              <TextField size="small" fullWidth label="End Date *" type="date" value={endDate}
                inputProps={{
                  min: startDate || selectedLeavePeriod?.startDate,
                  max: selectedLeavePeriod?.endDate,
                }}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setValidationMessage('');
                }} InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <TextField size="small" label="Number of Days" type="number" value={numberOfDays}
                onChange={(e) => setNumberOfDays(Number(e.target.value))}
                 InputProps={{readOnly: true}}
                inputProps={{ step: 0.5, min: 0.5 }} sx={{ width: 150 }}
              />
              <FormControlLabel
                control={<Switch checked={isHalfDay} onChange={(e) => {
                  setIsHalfDay(e.target.checked);
                  setValidationMessage('');
                }} />}
                label="Half Day"
                sx={{ display: selectedLeaveType?.allowHalfDay ? 'inline-flex' : 'none' }}
              />
              {isHalfDay && selectedLeaveType?.allowHalfDay && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Half Day Type</InputLabel>
                  <Select value={halfDayType} label="Half Day Type" onChange={(e) => {
                    setHalfDayType(e.target.value);
                    setValidationMessage('');
                  }}>
                    <MenuItem value="First Half">First Half</MenuItem>
                    <MenuItem value="Second Half">Second Half</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
           <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            label="Reason *"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setValidationMessage('');
            }}
            inputProps={{ maxLength: 250 }}
            helperText={`${reason.length}/250 characters`}
          />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || !isFormValid} sx={{ textTransform: 'none' }}>
            {loading ? <CircularProgress size={20} /> : 'Assign Leave'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AssignLeavePage;
